// One building's items from Amazon, or another party, on the Step 3 card for their own
// step (stepRecord.blockerItems): not received, partially received, then received, each under its category in
// its step's list, then those not applicable here, folded. Each says why it
// is needed, what came and from whom or its latest ask, what it holds up, and Late once
// past the date it was needed by, as the backend works them out
// (src/inrange_agents/amazon_items.py, docs/Y2026-Q4/design-needed-from-others.md); an open
// feedback point the other side owes is one of its items not received (needs.pointNeeds).
// Green once nothing in it is still needed. Step 1 keeps the general list.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type {
  AmazonBuilding,
  AmazonNeed,
  PointOrigin,
} from "../../api/hooks/useAmazonOverview";
import { CitedBody } from "../../components/CitedBody";
import citeStyles from "../../components/CitedBody.module.css";
import { useCitations } from "../../components/useCitations";
import { registerPath } from "../learningAgentPaths";
import { Chevron } from "../../components/Chevron";
import type { StoryStep } from "./blueprint";
import { OnlyTag, type OnlyWhere } from "./ContractText";
import { day, daysBetween, formatDate } from "./dates";
import { Editable } from "./editing";
import { HoldTag } from "./HoldTag";
import { GapTag } from "../../components/GapTag";
import {
  AnswerBlock,
  ItemActions,
  type ItemRef,
  OwnerChip,
} from "./ItemOwners";
import { itemKeyOf } from "../../api/hooks/useAmazonOwners";
import owners from "./itemOwners.module.css";
import {
  blocksItsStep,
  fromAmazon,
  itemReviewAttrs,
  NEED_STATUS,
  neededRuns,
  owed,
  stillNeededText,
} from "./needs";
import { needKey } from "./overrides";
import { itemNeededBy, neededByReason } from "./schedule";
import { siteCode } from "./siteCode";
import styles from "./amazonOverview.module.css";

/** The items only some buildings need that the list tags; a landlord item is not, as a
 * building with none already reads it not needed. */
const ONLY_TAG: Partial<Record<AmazonNeed["only_where"], OnlyWhere>> = {
  existing_pv: "existingPv",
  skylights: "skylights",
};

/** How many items show before the rest fold behind "Show N more". */
const SHOWN = 5;

const on = (iso: string): string => formatDate(day(iso));

const cite = (uploadId: string | undefined, quote = ""): string =>
  uploadId ? (quote ? `[${uploadId} "${quote.replace(/"/g, "'")}"]` : `[${uploadId}]`) : "";

/** What came and from whom, as the record shows it. */
const receivedLine = (n: AmazonNeed): string => {
  const from = n.supplied_by ? `From ${n.supplied_by}` : "Who gave it is not on record";
  return n.evidence ? `${from}, in ${n.evidence}` : `${from}; proof not on record`;
};

/** The latest ask for an item still to come, and any reply. */
const askLine = (n: AmazonNeed): string => {
  const ask = n.ask;
  if (!ask) return "";
  const who = [ask.by && `by ${ask.by}`, ask.to && `to ${ask.to}`].filter(Boolean).join(" ");
  // A viewer outside InRange has no upload link, so the email is named by its subject.
  const where = ask.upload_id
    ? ` in ${cite(ask.upload_id)}`
    : ask.subject
      ? ` in \u201c${ask.subject}\u201d`
      : "";
  const reply = ask.replied ? `; replied ${on(ask.replied)}` : "; no reply yet";
  return `Asked ${on(ask.sent)}${who ? ` ${who}` : ""}${where}${reply}`;
};

/** A request's citation, or for a viewer outside InRange (no ids) the words it quotes. */
const requestsLine = (n: AmazonNeed): string =>
  (n.sources ?? [])
    .filter((s) => s.kind === "request" && (s.upload_id || s.quote))
    .map((s) => (s.upload_id ? cite(s.upload_id, s.quote) : `\u201c${s.quote}\u201d`))
    .join(" ");

/** Who raised a feedback point, when, and in which email. */
const origin = (p: PointOrigin): string =>
  `${p.raised_by || "someone not on record"}${p.raised_on ? ` on ${on(p.raised_on)}` : ""}` +
  (p.source ? ` in \u201c${p.source}\u201d` : "");

export const NeededFromAmazon = ({
  b,
  items,
  stepId,
  steps,
  need,
  now,
  title,
}: {
  b: AmazonBuilding;
  items: AmazonNeed[];
  /** The blocker's own step: an item for another step names it and its own date. */
  stepId: string | null;
  steps: StoryStep[];
  /** Each step's needed-by date: an item is needed by when the step it is for must start. */
  need: Record<string, Date> | null;
  now: Date;
  /** Named for whoever owes the items where not given. */
  title?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const [openItems, setOpenItems] = useState<ReadonlySet<string>>(new Set());
  const toggleItem = (key: string) =>
    setOpenItems((was) => {
      const next = new Set(was);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const stepTitle = (id: string) => steps.find((s) => s.id === id)?.title ?? id;
  const projectId = b.project_id;
  // Always "Needed from Amazon": who owes each item is said on its own line (Noaa, 19 Oct
  // 2026), never as a contact's name in the heading.
  const heading = title ?? "Needed from Amazon";
  // Nothing still to come from the other side.
  const settled = !items.some(owed);
  const { runs, notNeeded } = neededRuns(items, stepId, stepTitle);
  // An item for another step names that step on a card covering several.
  const several = new Set(items.map((n) => n.step)).size > 1;
  const ordered = runs.flatMap((r) => r.groups.flatMap(([, list]) => list));
  const notNeededOrdered = notNeeded.flatMap(([, list]) => list);
  const shown = new Set((all ? ordered : ordered.slice(0, SHOWN)).map((n) => n.key));
  const bodies = useMemo(
    () => items.flatMap((n) => [receivedLine(n), askLine(n), requestsLine(n)]),
    [items],
  );
  const citations = useCitations(bodies);
  const itemRow = (n: AmazonNeed) => {
    const received = n.status === "received";
    // An item for another step says which, once by its group where that is the step's
    // name; not on a card whose items are all for the one step it stands for (Amazon
    // design inputs, all for Detailed design).
    const later = n.step !== stepId && !!n.category && (several || stepId === null);
    const by = owed(n) ? itemNeededBy(b, n, need) : null;
    const late = by !== null && by < now;
    // Why that date, lower-cased to follow "needed by".
    const because = by
      ? ` (${neededByReason(n, stepTitle).replace(/^./, (c) => c.toLowerCase())})`
      : "";
    const registers = (n.sources ?? []).filter((s) => s.kind === "register");
    const requests = requestsLine(n);
    const party = n.supplier && n.supplier !== "Amazon" ? n.supplier : "";
    const only = ONLY_TAG[n.only_where];
    // Everything under the status line folds behind "See more", closed by default (Noaa,
    // 20 Oct 2026): what came or the latest ask, feedback, waits, registers and requests.
    const more =
      (received ? !!receivedLine(n) : !!n.ask) ||
      (n.points ?? []).length > 0 ||
      (n.waits_for ?? []).length > 0 ||
      registers.length > 0 ||
      !!requests;
    const isOpen = openItems.has(n.key);
    const status = (
      <>
        {n.status === "partial" ? (
          <span className={styles.statusPartial}>{NEED_STATUS[n.status]}</span>
        ) : (
          NEED_STATUS[n.status]
        )}
        {received && (n.date ? ` ${on(n.date)}` : ", date not on record")}
      </>
    );
    return (
      // Text selected in it is a comment on the item, for its keeper to apply.
      <li
        key={n.key}
        {...itemReviewAttrs(b.site_code, n)}
      >
        {only && (
          <>
            <OnlyTag only={only} />{" "}
          </>
        )}
        {n.text}
        {n.clause && <span className={styles.source}> {n.clause}</span>}
        <div className={styles.askFacts}>
          {/* Only Amazon's list takes an override; an added item is its keeper's
      check. */}
          {n.listed === false ? (
            status
          ) : (
            <Editable
              target={{ kind: "need", projectId, key: n.key }}
              keys={[needKey(n.key, "status"), needKey(n.key, "date")]}
            >
              {status}
            </Editable>
          )}
          {[
            party ? `from ${party}` : "",
            later ? `for ${stepTitle(n.step)}` : "",
            by && (n.step !== stepId || n.due_step || n.due) && !late
              ? `needed by ${formatDate(by)}${because}`
              : "",
            n.point_from
              ? `asked for by ${origin(n.point_from)}`
              : n.listed === false && !n.due
                ? "asked for on this building"
                : "",
          ]
            .filter(Boolean)
            .map((x) => ` · ${x}`)
            .join("")}
          {late && by && (
            <span className={styles.itemLate}>
              {" "}
              · Late: needed by {formatDate(by)}, {daysBetween(by, now)} days ago
              {because}
            </span>
          )}
          {n.status === "partial" && n.partial && (
            <GapTag partial={n.partial} heading="Why it is partially received" />
          )}
          {owed(n) &&
            (blocksItsStep(n) ? (
              <HoldTag
                need={n}
                label={n.step === stepId ? "Holds this step up" : `Holds up ${stepTitle(n.step)}`}
              />
            ) : (
              <span className={styles.freeTag}>Doesn&apos;t hold it up</span>
            ))}
          {more && (
            <button
              type="button"
              className={styles.itemMore}
              aria-expanded={isOpen}
              onClick={() => toggleItem(n.key)}
            >
              {isOpen ? "See less" : "See more"} <Chevron open={isOpen} />
            </button>
          )}
        </div>
        {fromAmazon(n) && <ItemControls b={b} n={n} />}
        {more && isOpen && (
          <>
            {received && (
              <CitedBody
                className={styles.needLine}
                body={receivedLine(n)}
                citations={citations}
              />
            )}
            {!received && n.ask && (
              <CitedBody
                className={styles.needLine}
                body={askLine(n)}
                citations={citations}
              />
            )}
            {(n.points ?? []).length > 0 && (
              <div className={styles.needLine}>
                Asked for in feedback: {(n.points ?? []).map(origin).join("; ")}
              </div>
            )}
            {(n.waits_for ?? []).length > 0 && (
              <div className={styles.needLine}>
                Blocked by: {(n.waits_for ?? []).join("; ")}
              </div>
            )}
            {registers.length > 0 && (
              <div className={styles.needLine}>
                Stated in{" "}
                {registers.map((s, i) => (
                  <span key={s.line_id ?? s.label}>
                    {i > 0 && " "}
                    {/* Styled as the citations beside it ("Asked for in [...]"), so every
                        document on the card reads the same (Noaa, 19 Oct 2026). */}
                    {s.document_id ? (
                      <Link className={citeStyles.citation} to={registerPath(s.document_id, projectId)}>
                        [{s.label}]
                      </Link>
                    ) : (
                      `[${s.label}]`
                    )}
                  </span>
                ))}
              </div>
            )}
            {requests && (
              <CitedBody
                className={styles.needLine}
                body={`Asked for in ${requests}`}
                citations={citations}
              />
            )}
          </>
        )}
      </li>
    );
  };
  return (
    <div className={settled ? `${styles.needsCard} ${styles.needsCardDone}` : styles.needsCard}>
      <button
        type="button"
        className={styles.needsToggle}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {heading} ({items.length}) <span className={styles.askFacts}>{stillNeededText(items)}</span>{" "}
        <span className={styles.needsCaret}>
          <Chevron open={open} />
        </span>
      </button>
      <div hidden={!open}>
        {runs.map(({ run, groups }) => {
          const count = groups.reduce((sum, [, list]) => sum + list.length, 0);
          const visible = groups
            .map(([c, list]): [string, AmazonNeed[]] => [c, list.filter((n) => shown.has(n.key))])
            .filter(([, list]) => list.length > 0);
          if (!visible.length) return null;
          return (
            <div key={run.label}>
              {runs.length > 1 && (
                <p
                  className={
                    run.received
                      ? `${styles.needDivider} ${styles.needDividerReceived}`
                      : run.partial
                        ? `${styles.needDivider} ${styles.needDividerPartial}`
                        : styles.needDivider
                  }
                >
                  {run.label} ({count})
                </p>
              )}
              {visible.map(([c, list]) => (
                <div key={c}>
                  {c && (
                    <p className={styles.needCategory}>
                      {c} ({groups.find(([g]) => g === c)?.[1].length ?? list.length})
                    </p>
                  )}
                  <ol start={ordered.indexOf(list[0]) + 1}>{list.map(itemRow)}</ol>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {open && ordered.length > SHOWN && (
        <button type="button" className={styles.askToggle} onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show ${ordered.length - SHOWN} more`} <Chevron open={all} />
        </button>
      )}
      {open && notNeeded.length > 0 && (
        <div className={styles.needNotApplicable}>
          <button
            type="button"
            className={styles.askToggle}
            aria-expanded={showNotApplicable}
            onClick={() => setShowNotApplicable(!showNotApplicable)}
          >
            Not applicable here ({notNeededOrdered.length}){" "}
            <Chevron open={showNotApplicable} />
          </button>
          {showNotApplicable &&
            notNeeded.map(([c, list]) => (
              <div key={c}>
                {c && (
                  <p className={styles.needCategory}>
                    {c} ({list.length})
                  </p>
                )}
                <ol start={ordered.length + notNeededOrdered.indexOf(list[0]) + 1}>
                  {list.map(itemRow)}
                </ol>
              </div>
            ))}
        </div>
      )}
      {citations.modal}
    </div>
  );
};

/** Who owns an item needed from Amazon, Amazon's answer on it, and its actions, as on
 * Needed from Amazon (docs/Y2026-Q4/design-amazon-item-owners.md section 2.3). */
const ItemControls = ({ b, n }: { b: AmazonBuilding; n: AmazonNeed }) => {
  const item: ItemRef = {
    projectId: b.project_id,
    itemKey: itemKeyOf(n),
    text: n.text,
    siteCode: siteCode(b),
    owed: owed(n),
  };
  return (
    <>
      <AnswerBlock item={item} />
      <div className={owners.cardControls}>
        <OwnerChip item={item} />
        <ItemActions item={item} layout="row" />
      </div>
    </>
  );
};
